"use client"

import { useEffect, useRef, useState } from "react"
import { AlertTriangle, Check } from "lucide-react"
import { ToolPageShell, ToolProductCarousel, ToolShareBar } from "@/components/brand-kit"
import { GuideTool } from "@/components/tools/guide-tool"

// ---------- TOC sections ----------

const SECTIONS = [
  { id: "what", t: "The numbers" },
  { id: "zones", t: "Per-zone settings" },
  { id: "verify", t: "How to verify" },
  { id: "tune", t: "Tuning checklist" },
  { id: "signs", t: "When it's wrong" },
] as const

// USDA / FDA food-safety rules cited per docs/Page-Types-And-Templates.md (Temperature Safety).

export function TemperatureSettingOptimizerPageClient() {
  const [active, setActive] = useState<string>(SECTIONS[0].id)
  const observerRef = useRef<IntersectionObserver | null>(null)

  useEffect(() => {
    if (observerRef.current) observerRef.current.disconnect()
    observerRef.current = new IntersectionObserver(
      (entries) => {
        const visible = entries.filter((e) => e.isIntersecting)
        if (visible.length > 0) {
          const topmost = visible.reduce((prev, curr) =>
            prev.boundingClientRect.top < curr.boundingClientRect.top ? prev : curr,
          )
          setActive(topmost.target.id)
        }
      },
      { rootMargin: "-30% 0px -60% 0px", threshold: 0 },
    )
    SECTIONS.forEach((s) => {
      const el = document.getElementById(s.id)
      if (el) observerRef.current?.observe(el)
    })
    return () => observerRef.current?.disconnect()
  }, [])

  const jumpTo = (id: string) => {
    const el = document.getElementById(id)
    if (!el) return
    const y = el.getBoundingClientRect().top + window.scrollY - 96
    window.scrollTo({ top: y, behavior: "smooth" })
    setActive(id)
    // Keep the URL hash in sync so the entry is a real, shareable anchor
    // (plan §0 item 179a). replaceState avoids the native hash-jump.
    history.replaceState(null, "", `#${id}`)
  }

  return (
    <ToolPageShell
      toolName="Refrigerator Temperature Settings Guide"
      heroEyebrow="Temperature"
      heroTitlePre="What temperature should"
      heroTitleItalic="your fridge"
      heroTitlePost="be set to?"
      heroSubtitle="USDA's answer is exact: refrigerator at or below 40°F, freezer at 0°F. Here's how to dial those numbers in, verify them, and read the warning signs when something's off."
      heroMeta={["⏱ ~5 min read", "USDA Food Safety + DOE energy guidance"]}
      heroShare={<ToolShareBar title="What temperature should your fridge be set to?" />}
      heroRight={
        <div className="rounded-xl p-6 bg-gray-900 text-white">
          <div className="text-[11px] font-bold uppercase tracking-[0.15em] mb-4 text-[#ff9951]">
            The USDA rule
          </div>
          <p
            className="text-[17px] font-medium leading-[1.45] mb-5"
            style={{ textWrap: "balance" as never }}
          >
            Keep the fridge at or below{" "}
            <em className="italic text-[#ff9951]">40°F</em> and the freezer at{" "}
            <em className="italic text-[#ff9951]">0°F</em>.
          </p>
          <div className="grid grid-cols-2 gap-3 pt-4 border-t border-white/10">
            <div>
              <div className="text-[10px] uppercase tracking-[0.12em] text-white/50 font-bold">
                Fridge target
              </div>
              <div className="text-[22px] font-black tracking-tight">35–38°F</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.12em] text-white/50 font-bold">
                Freezer target
              </div>
              <div className="text-[22px] font-black tracking-tight">0°F</div>
            </div>
          </div>
        </div>
      }
      faqs={[
        {
          question: "Why exactly 40°F and 0°F?",
          answer:
            "Per USDA Food Safety and Inspection Service: bacterial growth slows dramatically below 40°F and effectively stops at 0°F. The fridge target is a ceiling, not a goal — aim for 35–38°F so brief door openings never push you above 40°F.",
        },
        {
          question: "Is colder safer?",
          answer:
            "Below ~33°F you start freezing produce, milk, and eggs. Below 0°F in the freezer doesn't extend safety meaningfully (food is already safe indefinitely at 0°F per USDA) but it does waste energy. Stick to the bands.",
        },
        {
          question: "How long does it take to confirm a temp change?",
          answer:
            "Allow at least 24 hours after any thermostat change. The mass of food inside slows the equilibration. Verifying sooner gives a misleading reading.",
        },
        {
          question: "Why is my temperature reading different in different spots?",
          answer:
            "It's supposed to vary slightly. The back wall is coldest (closest to the evaporator), the door bins are warmest (most exposure). USDA's 40°F ceiling refers to the warmest spot — usually the door — so calibrate there.",
        },
        {
          question: "Does temperature affect energy cost?",
          answer:
            "Yes. DOE estimates each degree colder than necessary adds 3–5% to annual energy use. Setting the fridge to 33°F instead of 37°F can add roughly 15% to your annual operating cost. Hit the band, don't beat it.",
        },
      ]}
      relatedTools={[
        {
          title: "Temperature Alarm Guide",
          category: "Temperature",
          description: "Decode the beep and silence it.",
          href: "/tools/refrigerator-temperature-alarm-guide",
          time: "~6 min",
        },
        {
          title: "Cooking Temperature Converter",
          category: "Temperature",
          description: "°F ↔ °C ↔ gas marks.",
          href: "/tools/cooking-temperature-converter",
          time: "~5 sec",
        },
        {
          title: "Power Outage Food Safety Guide",
          category: "Food Storage",
          description: "USDA 4hr/48hr rules during an outage.",
          href: "/tools/power-outage-food-safety-guide",
          time: "~4 min",
        },
        {
          title: "Energy Cost Calculator",
          category: "Energy & Cost",
          description: "Annual cost for any fridge.",
          href: "/tools/energy-cost-calculator",
          time: "~30 sec",
        },
      ]}
      cta={{
        title: "Temperature still drifting after every adjustment?",
        body: "Old gaskets, dirty coils, or a failing thermistor are the usual culprits. Use our diagnostic tools to find the cause — or replace the unit if it's past 10 years.",
        buttonLabel: "Check repair vs replace",
        buttonHref: "/tools/repair-or-replace-refrigerator",
      }}
    >
      {/* ============ ARTICLE BODY WITH STICKY TOC ============ */}
      <section className="py-8 md:py-12 bg-white">
        <div className="max-w-[1120px] mx-auto px-6 md:px-8 grid md:grid-cols-[170px_1fr] gap-8 md:gap-10">
          {/* Sticky TOC */}
          <aside className="hidden md:block">
            <div className="sticky top-[96px]">
              <div className="text-[10px] font-bold uppercase tracking-[0.15em] mb-3 text-[#ff9951]">
                On this page
              </div>
              <nav className="border-l border-gray-200" aria-label="Section navigation">
                {SECTIONS.map((s) => {
                  const isActive = active === s.id
                  return (
                    <a
                      key={s.id}
                      href={`#${s.id}`}
                      onClick={(e) => {
                        e.preventDefault()
                        jumpTo(s.id)
                      }}
                      aria-current={isActive ? "location" : undefined}
                      className={[
                        "block cursor-pointer py-2 pl-3 -ml-px transition-all text-left w-full",
                        isActive ? "text-gray-900" : "text-gray-500 hover:text-gray-700",
                      ].join(" ")}
                      style={{
                        borderLeft: isActive ? "2px solid #ff9951" : "2px solid transparent",
                      }}
                    >
                      <div className="text-[13px] font-bold leading-tight">{s.t}</div>
                    </a>
                  )
                })}
              </nav>
            </div>
          </aside>

          {/* Article body */}
          <article>
            <GuideTool
              eyebrow="Interactive dial advisor"
              title="What should the dial actually be?"
              intro="Describe the symptom and the load \u2014 get the setting change, not a generic number."
              questions={[
                            {
                              id: "symptom",
                              label: "What's the symptom?",
                              options: [
                                { value: "freezing", label: "Lettuce or drinks freezing" },
                                { value: "spoiling", label: "Milk spoils too fast" },
                                { value: "softice", label: "Ice cream stays soft" },
                                { value: "fine", label: "Nothing \u2014 just checking" }
                              ],
                            },
                            {
                              id: "load",
                              label: "How full does it run?",
                              options: [
                                { value: "packed", label: "Packed tight" },
                                { value: "normal", label: "Comfortably full" },
                                { value: "sparse", label: "Half empty" }
                              ],
                            }
              ]}
              compute={(a) => {
                            if (a.symptom === "freezing") {
                              return { tone: "warn", verdict: "You're set too cold \u2014 or blocking a vent", detail: "Raise the fridge setting one step toward 37\u00b0F and check nothing sits against the cold-air vent (usually top-left rear) \u2014 produce parked in the airflow freezes even at a correct set point." + (a.load === "packed" ? " A packed compartment makes vent-blocking much more likely \u2014 clear two inches around the vents first." : "") }
                            }
                            if (a.symptom === "spoiling") {
                              return { tone: "warn", verdict: "Run colder \u2014 verify 37\u00b0F with a thermometer", detail: "Drop the setting one step and measure with a glass-of-water thermometer after 24 hours; the target is 37\u00b0F, never above the USDA 40\u00b0F line. Milk lives on a middle shelf \u2014 the door is the warmest spot in the box and shortens its life by days." + (a.load === "sparse" ? " A half-empty box also swings warmer with each door open \u2014 a few water bottles add thermal mass." : "") }
                            }
                            if (a.symptom === "softice") {
                              return { tone: "warn", verdict: "Freezer to 0\u00b0F", detail: "Soft ice cream means the freezer is above 0\u00b0F. Step it down and give it 24 hours between changes; if it will not reach 0, check the door seal and the frost pattern before blaming the dial." }
                            }
                            return { tone: "good", verdict: "37\u00b0F fridge \u00b7 0\u00b0F freezer", detail: "The reference set points: 37\u00b0F in the fresh-food compartment (safely under USDA's 40\u00b0F line) and 0\u00b0F in the freezer. Verify with a thermometer once a season \u2014 dials drift, numbers don't." }
                          }}
              ctaLabel="Advise my dial"
              footnote="Targets: USDA 40\u00b0F line \u00b7 37\u00b0F/0\u00b0F reference set points."
            />
            {/* The numbers */}
            <section id="what" className="mb-12 md:mb-14 scroll-mt-24">
              <h2
                className="font-black text-gray-900 m-0 mb-5 pb-3 border-b border-gray-900"
                style={{ fontSize: "clamp(22px, 2.6vw, 28px)", letterSpacing: "-0.02em" }}
              >
                The numbers, exactly
              </h2>
              <p className="text-[17px] text-gray-700 leading-[1.7] mb-6">
                USDA Food Safety and Inspection Service is unambiguous: keep the refrigerator at or
                below <strong>40°F</strong>, the freezer at <strong>0°F</strong>. Above 40°F, bacterial
                growth accelerates inside the &ldquo;danger zone.&rdquo; At 0°F, food is safe
                indefinitely (though quality degrades over time).
              </p>
              <p className="text-[17px] text-gray-700 leading-[1.7] mb-6">
                The practical target is{" "}
                <em className="font-semibold not-italic text-[#ff9951]">35–38°F</em> for the fridge
                — that gives you a couple of degrees of headroom before brief door openings push
                you over 40°F.
              </p>

              <div
                className="rounded-xl p-5 md:p-6 my-8 flex gap-4 md:gap-5"
                style={{ background: "rgba(255,153,81,0.06)", border: "1px solid rgba(255,153,81,0.25)" }}
              >
                <div className="flex-shrink-0 w-12 h-12 rounded-full bg-[#ff9951] text-[var(--color-brand-action-text)] flex items-center justify-center">
                  <AlertTriangle className="w-5 h-5" />
                </div>
                <div>
                  <div className="font-black text-[17px] mb-1 text-gray-900">
                    The 40°F ceiling, not goal
                  </div>
                  <p className="text-[14px] text-gray-700 leading-[1.6]">
                    USDA&apos;s 40°F is a ceiling — the warmest spot at the warmest moment. Calibrate
                    your fridge so the warmest spot (usually a door bin) reads under 40°F, not the
                    coldest spot.
                  </p>
                </div>
              </div>
            </section>

            {/* Per-zone settings */}
            <section id="zones" className="mb-12 md:mb-14 scroll-mt-24">
              <h2
                className="font-black text-gray-900 m-0 mb-5 pb-3 border-b border-gray-900"
                style={{ fontSize: "clamp(22px, 2.6vw, 28px)", letterSpacing: "-0.02em" }}
              >
                Per-zone settings
              </h2>
              <div className="overflow-x-auto rounded-xl border border-gray-900">
                <table className="w-full text-[14px]">
                  <thead>
                    <tr className="bg-gray-900 text-white">
                      <th className="px-5 py-3 text-left text-[11px] uppercase tracking-[0.15em] font-bold">
                        Zone
                      </th>
                      <th className="px-5 py-3 text-left text-[11px] uppercase tracking-[0.15em] font-bold">
                        Target
                      </th>
                      <th className="px-5 py-3 text-left text-[11px] uppercase tracking-[0.15em] font-bold">
                        Source
                      </th>
                      <th className="px-5 py-3 text-left text-[11px] uppercase tracking-[0.15em] font-bold">
                        What it stores
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      ["Refrigerator (main)", "35–38°F", "USDA (≤40°F)", "Dairy, leftovers, meat, eggs"],
                      ["Freezer", "0°F", "USDA / FDA", "Frozen meat, prepared meals, ice"],
                      ["Crisper (high humidity)", "35–38°F", "USDA (≤40°F)", "Leafy greens, herbs"],
                      ["Crisper (low humidity)", "35–38°F", "USDA (≤40°F)", "Apples, stone fruit, peppers"],
                      ["Deli / meat drawer", "30–34°F", "USDA cold-chain guide", "Cured meats, deli, raw fish"],
                      ["Door bins", "≤40°F", "USDA (≤40°F)", "Condiments, butter, hard cheese"],
                    ].map((r, i, arr) => (
                      <tr
                        key={r[0]}
                        className={i % 2 ? "bg-gray-50" : "bg-white"}
                        style={{ borderBottom: i < arr.length - 1 ? "1px solid #e5e7eb" : "none" }}
                      >
                        <td className="px-5 py-4 font-extrabold tracking-tight">{r[0]}</td>
                        <td className="px-5 py-4">
                          <span className="font-mono font-bold text-[#ff9951]">{r[1]}</span>
                        </td>
                        <td className="px-5 py-4 text-gray-600">{r[2]}</td>
                        <td className="px-5 py-4 text-gray-700">{r[3]}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="text-[13px] text-gray-500 mt-4">
                Sources: USDA Food Safety and Inspection Service, FDA Food Code 2022.
              </p>
            </section>

            {/* How to verify */}
            <section id="verify" className="mb-12 md:mb-14 scroll-mt-24">
              <h2
                className="font-black text-gray-900 m-0 mb-5 pb-3 border-b border-gray-900"
                style={{ fontSize: "clamp(22px, 2.6vw, 28px)", letterSpacing: "-0.02em" }}
              >
                How to verify (don&apos;t trust the dial)
              </h2>
              <p className="text-[17px] text-gray-700 leading-[1.7] mb-6">
                Built-in fridge thermostats are notoriously imprecise — many use a single sensor on
                the back wall that reads the coldest spot, not the warmest. Use an independent
                appliance thermometer.
              </p>
              <ol className="list-none p-0 m-0 border-t border-gray-900">
                {[
                  {
                    n: "01",
                    t: "Get an appliance thermometer",
                    d: "Standalone unit, $5–15. Don't use a meat thermometer — they're slow to settle and not accurate at fridge temps.",
                  },
                  {
                    n: "02",
                    t: "Place a glass of water inside",
                    d: "Fill a glass with water, drop the thermometer in. The thermal mass damps out brief temperature swings from door openings.",
                  },
                  {
                    n: "03",
                    t: "Wait 24 hours",
                    d: "After any setting change, wait a full day before reading. The food mass inside slows equilibration.",
                  },
                  {
                    n: "04",
                    t: "Read the warmest spot",
                    d: "Place the glass in the warmest area (usually a door bin or top-front shelf). USDA's 40°F ceiling refers to the warmest spot.",
                  },
                  {
                    n: "05",
                    t: "Adjust by 1–2°F at a time",
                    d: "Don't make big swings. Step the dial down one increment, wait 24 hours, re-verify. Repeat until you're in the 35–38°F band.",
                  },
                ].map((s) => (
                  <li
                    key={s.n}
                    className="grid grid-cols-[auto_1fr] gap-5 sm:gap-7 items-start py-5 sm:py-6 border-b border-gray-900"
                  >
                    <div className="font-mono text-sm font-bold tracking-[0.05em] min-w-[28px] sm:min-w-[34px] pt-1 text-[#ff9951]">
                      {s.n}
                    </div>
                    <div>
                      <h3
                        className="text-gray-900"
                        style={{ fontSize: 22, fontWeight: 800, letterSpacing: "-0.02em" }}
                      >
                        {s.t}
                      </h3>
                      <p className="mt-1.5 text-[15px] text-gray-600 leading-[1.55]">{s.d}</p>
                    </div>
                  </li>
                ))}
              </ol>
            </section>

            {/* Tuning checklist */}
            <section id="tune" className="mb-12 md:mb-14 scroll-mt-24">
              <h2
                className="font-black text-gray-900 m-0 mb-5 pb-3 border-b border-gray-900"
                style={{ fontSize: "clamp(22px, 2.6vw, 28px)", letterSpacing: "-0.02em" }}
              >
                Tuning checklist
              </h2>
              <div className="grid sm:grid-cols-2 gap-3">
                {[
                  "Hit 35–38°F fridge / 0°F freezer per USDA",
                  "Verify the warmest spot, not the coldest",
                  "Vacuum condenser coils every 6 months",
                  "Don't overpack — 75% full is optimal for airflow",
                  "Keep at least 2\" of clearance behind the unit",
                  "Replace the door gasket every 8–10 years",
                  "Cool food on the counter before refrigerating",
                  "Verify after seasonal kitchen-temperature swings",
                ].map((t) => (
                  <div key={t} className="flex items-start gap-3 p-4 rounded-lg bg-gray-50">
                    <div className="w-6 h-6 rounded-full bg-[#0469f0] text-white flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Check className="w-3.5 h-3.5" />
                    </div>
                    <span className="text-[14px] text-gray-700 leading-[1.5]">{t}</span>
                  </div>
                ))}
              </div>
            </section>

            {/* When it's wrong */}
            <section id="signs" className="scroll-mt-24">
              <h2
                className="font-black text-gray-900 m-0 mb-5 pb-3 border-b border-gray-900"
                style={{ fontSize: "clamp(22px, 2.6vw, 28px)", letterSpacing: "-0.02em" }}
              >
                When something&apos;s off
              </h2>
              <p className="text-[17px] text-gray-700 leading-[1.7] mb-6">
                Three patterns to read against the dial.
              </p>
              <div className="space-y-3">
                {[
                  {
                    t: "Fridge too cold",
                    d: "Lettuce or milk freezing, frost on shelves, ice crystals in produce. Step the dial up 1–2°F.",
                  },
                  {
                    t: "Fridge too warm",
                    d: "Milk spoils early, condensation on inside walls, butter stays soft on the door. Step the dial down 1–2°F. Check the door gasket if it persists.",
                  },
                  {
                    t: "Freezer too warm",
                    d: "Ice cream stays soft, ice crystals on packaged food, frost buildup. Verify with a thermometer — should read 0°F or below at the warmest spot.",
                  },
                ].map((r, i) => (
                  <div
                    key={r.t}
                    className="grid grid-cols-[36px_1fr] sm:grid-cols-[44px_1fr] gap-4 sm:gap-5 p-4 sm:p-5 rounded-lg"
                    style={{ background: i === 0 ? "rgba(255,153,81,0.04)" : "#f9fafb" }}
                  >
                    <div
                      className="font-mono font-bold"
                      style={{
                        fontSize: 14,
                        letterSpacing: "0.05em",
                        color: i === 0 ? "#ff9951" : "#9ca3af",
                        lineHeight: 1.4,
                      }}
                    >
                      {String(i + 1).padStart(2, "0")}
                    </div>
                    <div>
                      <h3 className="text-[18px] font-extrabold tracking-[-0.015em] mb-1 text-gray-900">
                        {r.t}
                      </h3>
                      <p className="text-[14px] text-gray-600 leading-[1.55]">{r.d}</p>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          </article>
        </div>
      </section>

      {/* ============ PRIMARY CAROUSEL ============ */}
      <section className="bg-white py-4 md:py-6">
        <ToolProductCarousel
          eyebrow="Better temperature control"
          title="French door fridges with sealed thermistors"
          description="Newer ENERGY STAR units hold tighter temp bands than older single-sensor models."
          collectionType="french-door"
          fallbackCollectionType="bottom-freezer"
        />
      </section>

      {/* ============ SECONDARY CAROUSEL ============ */}
      <section className="bg-white py-4 md:py-6">
        <ToolProductCarousel
          eyebrow="You might also like"
          title="Side-by-sides with dual-zone control"
          description="Independent fridge / freezer thermostats — easier to dial each side in."
          collectionType="side-by-side"
          fallbackCollectionType="top-freezer"
        />
      </section>
    </ToolPageShell>
  )
}
